import React, { useMemo, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PrivacyPolicyModal } from '../components/modals/PrivacyPolicyModal';
import { HomeValuationForm } from '../components/sections/HomeValuationForm';
import { LandingBlock } from '../components/landing/LandingBlocks';
import { LandingHero, LandingClosing, JessBio, MoreGuides, Eyebrow } from '../components/landing/LandingSections';
import { NAV_ITEMS } from '../constants/nav';
import { findLandingPage } from '../constants/landingPages';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://jesssellseastcoast.com';

const ValuationBand = ({ page, openPrivacyPolicy }) => (
  <section id="home-value-form" className="bg-[#143160] py-16 sm:py-20 px-5" data-testid="landing-valuation-band">
    <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
      <div className="text-white space-y-4 text-center lg:text-left">
        <Eyebrow light>Free Home Valuation</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{page.closing.h2}</h2>
        <p className="text-white/80 leading-relaxed">{page.closing.text}</p>
      </div>
      <div className="w-full max-w-lg mx-auto lg:ml-auto">
        <HomeValuationForm openPrivacyPolicy={openPrivacyPolicy} />
      </div>
    </div>
  </section>
);

export default function LandingPage() {
  const slug = useLocation().pathname.replace(/^\/|\/$/g, '');
  const page = findLandingPage(slug);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const jsonLd = useMemo(() => (page ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: page.h1,
    description: page.description,
    author: { '@type': 'Person', name: 'Jessica Burnette', url: SITE },
    publisher: { '@type': 'Organization', name: 'Jess Sells East Coast', logo: { '@type': 'ImageObject', url: `${SITE}/img/logo.webp` } },
    mainEntityOfPage: `${SITE}/${page.slug}`,
  } : null), [page]);
  useSeo({ title: page?.title || '', description: page?.description || '', path: `/${slug}`, jsonLd });

  if (!page) return <Navigate to="/" replace />;
  const openPrivacyPolicy = () => setShowPrivacy(true);

  return (
    <div className="min-h-screen bg-white text-neutral-800" data-testid={`landing-page-${page.slug}`}>
      <Navbar navItems={NAV_ITEMS} />
      <LandingHero page={page} />

      <article>
        {page.sections.map((blocks, i) => (
          <section key={blocks[0].text} className={`py-14 sm:py-16 ${i % 2 ? 'bg-sand-50' : 'bg-white'}`}>
            <div className="max-w-3xl mx-auto px-5 space-y-5">
              {blocks.map((block, j) => <LandingBlock key={`${block.type}-${j}`} block={block} />)}
            </div>
          </section>
        ))}
      </article>

      <LandingClosing closing={page.closing} />
      {page.kind === 'neighborhood' && <ValuationBand page={page} openPrivacyPolicy={openPrivacyPolicy} />}
      <JessBio />
      <MoreGuides currentSlug={page.slug} />

      <Footer navItems={NAV_ITEMS} openPrivacyPolicy={openPrivacyPolicy} />
      <PrivacyPolicyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
